import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyViewDetails, SurveyViewDetailsDTO } from "./surveyViewDetails";
import { SurveyDetailsAndDashboardViews, SurveyDetailsAndDashboardViewsDTO } from "./surveyDetailsAndDashboardViews";
/* eslint-disable import/export */
export abstract class EditDashboardViewRPC implements
  LeoRPC<EditDashboardViewRPC.Request, EditDashboardViewRPC.Response, EditDashboardViewRPC.Errors.Errors>
{
  abstract execute(
    request: EditDashboardViewRPC.Request
  ): Promise<LeoRPCResult<EditDashboardViewRPC.Response, EditDashboardViewRPC.Errors.Errors>>;
}

export namespace EditDashboardViewRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
    surveyViewDetails: SurveyViewDetailsDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isEditDashboardViewRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly surveyViewDetails: SurveyViewDetails;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    surveyViewDetails: SurveyViewDetails
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      this.surveyViewDetails = surveyViewDetails;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const surveyViewDetails = SurveyViewDetails.fromDTO(getObject(dto, "surveyViewDetails", InvalidRequestSurveyViewDetailsError));
      return new Request(
        projectId,
        surveyId,
        surveyViewId,
        surveyViewDetails
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          surveyViewDetails: this.surveyViewDetails.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `EditDashboardViewRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',surveyViewDetails='${this.surveyViewDetails}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViewsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isEditDashboardViewResponse: boolean = true;
  
    readonly surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews;
  
  
    constructor(
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews
    ) {
      this.surveyDetailsAndDashboardViews = surveyDetailsAndDashboardViews;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndDashboardViews = SurveyDetailsAndDashboardViews.fromDTO(getObject(dto, "surveyDetailsAndDashboardViews", InvalidResponseSurveyDetailsAndDashboardViewsError));
      return new Response(
        surveyDetailsAndDashboardViews
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndDashboardViews: this.surveyDetailsAndDashboardViews.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `EditDashboardView(surveyDetailsAndDashboardViews='${this.surveyDetailsAndDashboardViews}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    SurveyViewAlreadyExists = "SURVEY_VIEW_ALREADY_EXISTS",
    MasterSurveyViewCannotBeEdited = "MASTER_SURVEY_VIEW_CANNOT_BE_EDITED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface SurveyViewAlreadyExistsDTO {
      code: string;
    }
    
    export class SurveyViewAlreadyExists extends LeoRPCError<RPCError> {
      readonly isSurveyViewAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyViewAlreadyExists;
        super(code);
      }
    
    
      toDTO(): SurveyViewAlreadyExistsDTO {
        const dto: SurveyViewAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyViewAlreadyExists.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.SURVEY_VIEW_ALREADY_EXISTS()`;
    };
    
    
    export interface MasterSurveyViewCannotBeEditedDTO {
      code: string;
    }
    
    export class MasterSurveyViewCannotBeEdited extends LeoRPCError<RPCError> {
      readonly isMasterSurveyViewCannotBeEdited: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MasterSurveyViewCannotBeEdited;
        super(code);
      }
    
    
      toDTO(): MasterSurveyViewCannotBeEditedDTO {
        const dto: MasterSurveyViewCannotBeEditedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MasterSurveyViewCannotBeEdited.prototype.toString = function toString(): string {
      return `EditDashboardViewRPC.MASTER_SURVEY_VIEW_CANNOT_BE_EDITED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | InvalidSurveyViewId | SurveyViewAlreadyExists | MasterSurveyViewCannotBeEdited;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyViewIdDTO | SurveyViewAlreadyExistsDTO | MasterSurveyViewCannotBeEditedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.SurveyViewAlreadyExists:
          return new SurveyViewAlreadyExists();
        case RPCError.MasterSurveyViewCannotBeEdited:
          return new MasterSurveyViewCannotBeEdited();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends EditDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends EditDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends EditDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestSurveyViewDetailsError extends EditDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewDetailsError";
  }
}

export class InvalidResponseSurveyDetailsAndDashboardViewsError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndDashboardViewsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseSurveyViewAlreadyExistsError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyViewAlreadyExistsError";
  }
}

export class InvalidResponseMasterSurveyViewCannotBeEditedError extends EditDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMasterSurveyViewCannotBeEditedError";
  }
}

